import React, {useMemo, useState, memo} from "react";
import {useTranslation} from "react-i18next";
import {Form, Formik} from "formik";
// local
import {ButtonTransparent} from "../../../buttons/ButtonTransparent";
import FormikField from "components/FormikField";
import {RadioGroup} from "components/RadioGroup";
import {schema, initialValues} from "../components/invoicingFormAttrs";
import {Modal} from "components/Modal";
import {LabelData} from "components/LabelData";
import {IconMailLetter} from "components/icons";
import {
  hasTranslation,
  createLanguageKeysArray,
  subscriptionPrice,
  getFullName,
} from "helpers/helper";
import {subscriptionPriceType, subscriptionPricePeriods} from "constants/user.consts";
import CustomRadioSwitch from "components/CustomRadioSwitch";

const formikFieldProps = {
  labelDataMode: true,
  width: "230px",
  className: "input",
  addClass: "mb-4",
};

const SubscriptionOptionsData = memo(({subscription, options, t}) => (
  <React.Fragment>
    <div className="fz-13 mt-2">{t(subscription.descriptionKey)}</div>
    <ul className="pl-3 mt-4">
      {!!options?.length &&
        options
          .filter((item) => hasTranslation(item))
          .map((item) => (
            <li className="subscription__option" key={item}>
              <span className="truncate-me">{t(item)}</span>
            </li>
          ))}
    </ul>
  </React.Fragment>
));

export const SubscriptionModalOwner = ({
  currentSubscription,
  organization,
  username,
  email,
  purchasedUser,
  onClose,
  onClickSubmit,
}) => {
  const {t} = useTranslation();

  const [activePeriod, setActivePeriod] = useState(
    currentSubscription.pricePeriod ?? currentSubscription.pricePeriodDefault
  );
  const [loading, setLoading] = useState(false);

  const pricePeriods = useMemo(
    () =>
      subscriptionPricePeriods.filter(
        (item) => ![null, undefined].includes(currentSubscription[item.priceKey])
      ),
    [currentSubscription]
  );
  const isSinglePeriod = useMemo(() => pricePeriods.length < 2, [pricePeriods]);

  const radioValues = useMemo(
    () =>
      pricePeriods.map((item) => ({
        ...item,
        key: item.periodKey,
        value: item.periodKey,
        label: `SUBSCRIPTION_TAB_MODAL_CONFIRM_${item.periodKey}_PRICE_EXPLANATION`,
        checked: item.periodKey === activePeriod,
      })),
    [pricePeriods, activePeriod]
  );

  const subscriptionOptions = useMemo(
    () =>
      createLanguageKeysArray(
        currentSubscription.optionsCount,
        `${currentSubscription.name}_OPTION`
      ),
    [currentSubscription]
  );

  const singlePriceData = useMemo(
    () =>
      `${subscriptionPrice(currentSubscription, activePeriod)} ${t(
        subscriptionPriceType[currentSubscription.priceType] ??
          subscriptionPriceType.PRICE_PER_MONTH
      )}`,
    [activePeriod, currentSubscription, t]
  );

  const SubscriptionPriceData = () =>
    isSinglePeriod ? (
      <div className="mb-2">{singlePriceData}</div>
    ) : (
      <RadioGroup
        values={radioValues}
        resultValue={setActivePeriod}
        bottomData={pricePeriods.map(
          (item) =>
            `${currentSubscription[item.priceKey]?.replace(".00", "") ?? "-"} ${t(
              subscriptionPriceType[currentSubscription.priceType] ??
                subscriptionPriceType.PRICE_PER_MONTH
            )}`
        )}
        addClassDataWrapper="pa-2"
        addClassItemDataWrapper={pricePeriods.map((item) =>
          item.periodKey === activePeriod ? "border-link" : "border-hover"
        )}
        addClassBottomData="mt-1"
      />
    );

  const onSubmit = (values, {resetForm}) => {
    setLoading(true);
    onClickSubmit({
      ...values,
      pricePeriod: activePeriod,
      userId: purchasedUser?.UserId ?? null,
    })
      .then(() => {
        resetForm();
        onClose();
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      titleText={"SUBSCRIPTION_TAB_MODAL_CONFIRM_HEADING"}
      hugeTitle
      boldTitle
      titleIcon={null}
      noCloseButton
      noCloseOnEsc
      width={700}
      rounded
      whiteTitle
      addClass="subscriptions-modal"
      onClose={onClose}
    >
      <Formik
        initialValues={{...initialValues, email, reference: username}}
        validationSchema={schema}
        onSubmit={onSubmit}
      >
        {({values, setFieldValue}) => (
          <Form>
            <div className="df-row-jsb" style={{width: "100%"}}>
              <div className="mobile-column mr-8">
                <LabelData
                  label="SUBSCRIPTION_TAB_MODAL_CONFIRM_LABEL_01"
                  data={t(currentSubscription.name)}
                  boldData
                  additionalData={
                    <SubscriptionOptionsData
                      subscription={currentSubscription}
                      options={subscriptionOptions}
                      t={t}
                    />
                  }
                  addClass="mb-8"
                />
                <LabelData
                  label="SUBSCRIPTION_TAB_MODAL_CONFIRM_LABEL_02"
                  data={<SubscriptionPriceData />}
                  explanation={`SUBSCRIPTION_TAB_MODAL_CONFIRM_${activePeriod}_PRICE_EXPLANATION`}
                  explanationInfo={`SUBSCRIPTION_TAB_MODAL_CONFIRM_${activePeriod}_PRICE_EXPLANATION_INFO`}
                  addClass="mb-8"
                />
              </div>
              <div className="mobile-column">
                {!!purchasedUser && (
                  <LabelData
                    label="SUBSCRIPTION_TAB_MODAL_CONFIRM_LABEL_08"
                    data={
                      <React.Fragment>
                        {getFullName(
                          purchasedUser.User?.firstName,
                          purchasedUser.User?.lastName
                        )}
                        <div className="mt-1 fz-12">{purchasedUser.User?.email}</div>
                      </React.Fragment>
                    }
                    boldData
                    addClass="mb-4"
                  />
                )}
                <LabelData
                  label="SUBSCRIPTION_TAB_MODAL_CONFIRM_LABEL_03"
                  data={
                    <React.Fragment>
                      {organization.Company.companyName}
                      <div className="mt-1 fz-12">
                        {organization.Company.companyNumber}
                      </div>
                    </React.Fragment>
                  }
                  boldData
                  addClass="mb-4"
                />
                <FormikField
                  {...formikFieldProps}
                  name="email"
                  label={t("SUBSCRIPTION_TAB_MODAL_CONFIRM_LABEL_05")}
                  labelInfo={t("SUBSCRIPTION_TAB_MODAL_CONFIRM_LABEL_05_INFO")}
                  highlightIfEmpty={!values.email}
                  placeholder={t("TYPE_TO_ADD_EMAIL")}
                  errorName="email"
                  prefixIcon={<IconMailLetter />}
                />
                <FormikField
                  {...formikFieldProps}
                  name="reference"
                  label={t("SUBSCRIPTION_TAB_MODAL_CONFIRM_LABEL_06")}
                  labelInfo={t("SUBSCRIPTION_TAB_MODAL_CONFIRM_LABEL_06_INFO")}
                  placeholder={t("TYPE_TO_ADD_REFERENCE")}
                />
                <FormikField
                  {...formikFieldProps}
                  name="discountCode"
                  label={t("SUBSCRIPTION_TAB_MODAL_CONFIRM_LABEL_07")}
                  labelInfo={t("SUBSCRIPTION_TAB_MODAL_CONFIRM_LABEL_07_INFO")}
                  placeholder={t("TYPE_TO_ADD_POSSIBLE_CODE")}
                />
              </div>
            </div>
            <CustomRadioSwitch
              label={`${t("I_REPRESENT")} ${username} ${t("REPRESENT")} ${
                organization.Company.companyName
              }`}
              small
              mediumText
              checked={values.isRepresent}
              isActive={(checked) => setFieldValue("isRepresent", checked)}
            />
            <CustomRadioSwitch
              label={
                <React.Fragment>
                  {t("SUBSCRIPTION_TAB_MODAL_CONFIRM_SWITCH_02_01")}
                  <a
                    href="https://www.handyday.com/allmanna-villkor"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("SUBSCRIPTION_TAB_MODAL_CONFIRM_SWITCH_02_02")}
                  </a>
                </React.Fragment>
              }
              small
              mediumText
              addWrapperClass="mb-6 mt-4"
              checked={values.isTerms}
              isActive={(checked) => setFieldValue("isTerms", checked)}
            />
            <div className="subscriptions-modal__buttons">
              <ButtonTransparent
                medium
                name={t("CANCEL")}
                onClick={onClose}
                color="red"
              />
              <ButtonTransparent
                type="submit"
                addClass="ml-4"
                name={t("SUBSCRIPTION_TAB_MODAL_SUBSCRIBE_BUTTON")}
                loading={loading}
                disabled={!(values.email && values.isRepresent && values.isTerms)}
                medium
                nonTransparent
              />
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};
